import { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from '../../components/Dropdown';
import { SelectablePill } from '../../components/SelectablePill';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import {
  Activities,
  ActivityLocation,
  ActivityRow,
  ActivityText,
  ActivityTitle,
  Column,
  Columns,
  Container,
  DateGroup,
  DateLabel,
  Header,
  HeaderLabel,
  HeaderName,
  StaticHeader,
  TriggerValue,
} from './ItineraryView.styles';
import type { ItineraryGuest, ItineraryViewProps } from './ItineraryView.types';

const DEFAULT_SINGLE_COLUMN_MAX_WIDTH = 640;
const MAX_COLUMNS = 2;

function reconcileSelection(previous: string[], guestIds: string[], columnCount: number) {
  const result: string[] = [];
  for (let i = 0; i < columnCount; i++) {
    const candidate = previous[i];
    const isUsable = candidate && guestIds.includes(candidate) && !result.includes(candidate);
    result.push(isUsable ? candidate : guestIds.find((id) => !result.includes(id)) ?? guestIds[0]);
  }
  return result;
}

export function ItineraryView({
  guests,
  selectGuestLabel = 'Select guest',
  singleColumnMaxWidth = DEFAULT_SINGLE_COLUMN_MAX_WIDTH,
  className,
}: ItineraryViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useContainerWidth(containerRef);

  const guestsWithItems = useMemo<ItineraryGuest[]>(
    () => guests.filter((guest) => guest.days.some((day) => day.activities.length > 0)),
    [guests]
  );

  const guestIds = useMemo(() => guestsWithItems.map((guest) => guest.id), [guestsWithItems]);
  const guestIdsKey = guestIds.join('|');

  const isSingleColumn = width === undefined ? true : width <= singleColumnMaxWidth;
  const columnCount = isSingleColumn ? 1 : Math.min(MAX_COLUMNS, guestsWithItems.length);
  const showDropdowns = guestsWithItems.length > columnCount;

  const [selected, setSelected] = useState<string[]>(() =>
    reconcileSelection([], guestIds, columnCount)
  );

  useEffect(() => {
    setSelected((prev) =>
      reconcileSelection(prev, guestIdsKey ? guestIdsKey.split('|') : [], columnCount)
    );
  }, [guestIdsKey, columnCount]);

  if (guestsWithItems.length === 0) return null;

  return (
    <Container ref={containerRef} className={className}>
      <Columns $columns={columnCount}>
        {selected.map((guestId, columnIndex) => {
          const guest = guestsWithItems.find((candidate) => candidate.id === guestId);
          if (!guest) return null;

          return (
            <Column key={columnIndex} data-testid="itinerary-column">
              <Header>
                {showDropdowns ? (
                  <Dropdown
                    align="left"
                    aria-label={selectGuestLabel}
                    value={guest.id}
                    onChange={(value) =>
                      setSelected((prev) => prev.map((id, i) => (i === columnIndex ? value : id)))
                    }
                    options={guestsWithItems.map((option) => ({
                      value: option.id,
                      label: option.name,
                      disabled: selected.includes(option.id) && option.id !== guest.id,
                    }))}
                    renderValue={(option) => (
                      <TriggerValue>
                        <HeaderLabel>{selectGuestLabel}</HeaderLabel>
                        <HeaderName>{option.label}</HeaderName>
                      </TriggerValue>
                    )}
                  />
                ) : (
                  <StaticHeader>
                    <HeaderLabel>{selectGuestLabel}</HeaderLabel>
                    <HeaderName>{guest.name}</HeaderName>
                  </StaticHeader>
                )}
              </Header>

              {guest.days.map((day) => (
                <DateGroup key={day.date} data-single={isSingleColumn}>
                  <DateLabel>{day.date}</DateLabel>
                  <Activities>
                    {day.activities.map((activity, activityIndex) => (
                      <ActivityRow key={activity.id ?? `${activity.time}-${activityIndex}`}>
                        <SelectablePill variant="display" label={activity.time} />
                        <ActivityText>
                          <ActivityTitle>{activity.title}</ActivityTitle>
                          {activity.location && (
                            <ActivityLocation>{activity.location}</ActivityLocation>
                          )}
                        </ActivityText>
                      </ActivityRow>
                    ))}
                  </Activities>
                </DateGroup>
              ))}
            </Column>
          );
        })}
      </Columns>
    </Container>
  );
}
